import { Eye, EyeOff } from 'lucide-react';
import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * A password field with a show/hide toggle.
 *
 * Drop-in for `<Input type="password" />`. Like `Input`, it is rendered inside
 * a Radix `Slot` by `form.tsx`, which clones its child and passes a `ref` —
 * the ref must reach the real `<input>`, so it is forwarded rather than held
 * here.
 *
 * Accessibility notes, because a toggle like this is easy to ship half-built:
 *  - the control is a real `<button type="button">`, so it is reachable by
 *    keyboard and cannot submit the form it sits inside;
 *  - its accessible name changes with state ("Show password" / "Hide
 *    password") and `aria-pressed` carries the state itself, so a screen
 *    reader user knows whether the password is currently exposed;
 *  - the icon is `aria-hidden` — the button's label already says it;
 *  - right padding on the input keeps long values from running under the
 *    button.
 *
 * The field always starts masked. Nothing persists the revealed state across
 * mounts: someone who revealed a password once should not have the next form
 * open in the clear, possibly in front of other people.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ className, ...props }, ref) {
    const [visible, setVisible] = useState(false);
    const describedBy = useId();

    return (
      <div className="relative">
        <Input
          {...props}
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cn('pr-9', className)}
          aria-describedby={describedBy}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute inset-y-0 right-0 flex w-9 items-center justify-center rounded-r-md transition-colors focus-visible:outline-none focus-visible:ring-1"
          // A password field is not a tab stop people expect to fight with;
          // the toggle sits after the input in DOM order so Tab reaches it
          // naturally on the way to the submit button.
        >
          {visible ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
        <span id={describedBy} className="sr-only">
          {visible ? 'Password is visible' : 'Password is hidden'}
        </span>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
